import { PersonalViewStatus } from "../../components/PersonalViewNotice";
import type { PersonalViewState } from "../../lib/usePersonalView";
import FilterDropdown from "../../components/FilterDropdown";
import DateInput from "../../components/DateInput";
import { formatDateShort } from "../../lib/format";

import {
  ARTICLE_DETAIL_PERIOD_OPTIONS,
  type ArticleDetailPeriod,
  type ArticleDetailPeriodMode,
  type ArticleDetailPeriodSelection,
} from "./period";
import { rollingMonthCutoffIso } from "./savings";

export function ArticlePeriodControl({
  selection,
  period,
  anchorDate,
  onChange,
  personalView,
}: {
  personalView: PersonalViewState;
  selection: ArticleDetailPeriodSelection;
  period: ArticleDetailPeriod;
  anchorDate: string | null;
  onChange: (selection: ArticleDetailPeriodSelection) => void;
}) {
  function changeMode(mode: ArticleDetailPeriodMode) {
    if (mode !== "custom") {
      onChange({ ...selection, mode });
      return;
    }
    onChange({
      mode,
      customFrom:
        selection.customFrom || rollingMonthCutoffIso(anchorDate, 12) || "",
      customTo: selection.customTo || anchorDate || "",
    });
  }

  return (
    <section
      aria-labelledby="article-period-heading"
      className="min-w-0"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2
            id="article-period-heading"
            className="text-lg font-semibold tracking-tight text-slate-900"
          >
            Einkaufspreise
          </h2>
          {period.isValid ? (
            <span className="text-sm tabular-nums text-slate-600" title={`Letzter verfügbarer Beleg ${formatDateShort(anchorDate)}`} aria-live="polite">
              {period.rangeLabel}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="article-period-actions">
        <FilterDropdown
          label="Zeitraum"
          ariaLabel="Artikelzeitraum wählen"
          options={ARTICLE_DETAIL_PERIOD_OPTIONS}
          value={selection.mode}
          defaultValue="rolling36m"
          showValue
          onChange={changeMode}
        />
        {/* Nur sichtbar, wenn vom Standard abgewichen wurde (#630). */}
        {selection.mode !== "rolling36m" ? <button type="button" disabled={!personalView.ready || personalView.pending} onClick={personalView.reset} title="Drei Jahre bis zum letzten verfügbaren Beleg" className="inline-flex h-9 items-center text-sm font-medium text-emerald-700 hover:underline disabled:opacity-50">Zurücksetzen</button> : null}
        </div>
      </div>

      <div className="mt-2"><PersonalViewStatus view={personalView} draftMessage={personalView.ready && selection.mode === "custom" && !period.isValid ? "Dieser Zeitraum ist noch nicht gültig und wird nicht gemerkt. Deine zuletzt gemerkte Auswahl bleibt erhalten." : undefined} /></div>

      {selection.mode === "custom" ? (
        <div className="mt-3 flex flex-wrap gap-3">
          <label className="block min-w-[9rem] text-xs font-medium text-slate-600">
            <span className="mb-1 block">Von</span>
            <DateInput
              value={selection.customFrom}
              ariaLabel="Artikelzeitraum von wählen"
              placeholder="Von"
              onChange={(customFrom) => onChange({ ...selection, customFrom })}
            />
          </label>
          <label className="block min-w-[9rem] text-xs font-medium text-slate-600">
            <span className="mb-1 block">Bis</span>
            <DateInput
              value={selection.customTo}
              ariaLabel="Artikelzeitraum bis wählen"
              placeholder="Bis"
              onChange={(customTo) => onChange({ ...selection, customTo })}
            />
          </label>
        </div>
      ) : null}

      {period.error ? (
        <p role="alert" className="mt-1 text-xs font-medium text-rose-700">
          {period.error}
        </p>
      ) : null}
    </section>
  );
}
